import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, Linkedin, Twitter, Facebook } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import SEO, { StructuredData } from '../components/SEO';

import { API_URL } from '../config/api';
const API = `${API_URL}/api`;
const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";
const SITE_URL = 'https://batiassist.com';

// Sample post content
const samplePostContent = `
<h2>Introduction</h2>
<p>En tant qu'artisan, tu jongles entre les chantiers, les clients, l'administratif et la prospection. Cette charge de travail peut rapidement devenir écrasante si tu ne mets pas en place une organisation efficace.</p>

<h2>1. Définir ses priorités</h2>
<p>La première étape est d'identifier ce qui est vraiment important dans ton activité. Pose-toi les bonnes questions :</p>
<ul>
<li>Quelles tâches génèrent le plus de valeur ?</li>
<li>Quelles tâches peuvent être déléguées ?</li>
<li>Quelles tâches peuvent être automatisées ?</li>
</ul>

<h2>2. Structurer son temps</h2>
<p>Bloquez des créneaux dédiés pour chaque type d'activité : chantiers, administratif, prospection, formation. Évitez de mélanger les tâches pour rester concentré et efficace.</p>

<h2>3. Déléguer intelligemment</h2>
<p>Tu ne peux pas tout faire seul. Identifiez les tâches qui ne nécessitent pas ton expertise directe et confiez-les à une assistante ou à des outils automatisés.</p>

<h2>4. Utiliser les bons outils</h2>
<p>Adoptez des outils digitaux pour gérer tes devis, ton planning, tes factures et ta communication client. Ces outils te feront gagner un temps précieux.</p>

<h2>Conclusion</h2>
<p>Organiser son entreprise demande un investissement initial en temps et en réflexion, mais les bénéfices sont immenses : moins de stress, plus de productivité et une meilleure qualité de vie.</p>

<p><strong>Besoin d'aide pour structurer ton entreprise ?</strong> BatiAssist te propose un assistant à mi-temps ou plein temps et une équipe digitale pour toi accompagner.</p>
`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        // Use sample post if not found
        setPost({
          id: '1',
          title: 'Comment organiser son entreprise quand on est artisan',
          slug: slug,
          excerpt: 'Découvre les meilleures pratiques pour structurer ton activité d\'artisan.',
          content: samplePostContent,
          image_url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200',
          author: 'Romain Trallero',
          category: 'Organisation',
          created_at: new Date().toISOString(),
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = window.location.href;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-rm-text mb-4">Article non trouvé</h1>
          <Link to="/blog">
            <Button>Retour au blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Prepare article SEO data
  const articleSeoData = {
    title: post.title,
    description: post.excerpt,
    image: post.image_url,
    publishedTime: post.created_at,
    modifiedTime: post.updated_at || post.created_at,
    author: post.author,
    url: `${SITE_URL}/blog/${post.slug}`
  };

  return (
    <div data-testid="blog-post-page">
      <SEO
        title={`${post.title} | Blog BatiAssist`}
        description={post.excerpt}
        image={post.image_url}
        path={`/blog/${post.slug}`}
        type="article"
        article={{
          publishedTime: post.created_at,
          modifiedTime: post.updated_at || post.created_at,
          author: post.author,
          tags: [post.category]
        }}
      />
      <StructuredData type="article" data={articleSeoData} />
      
      {/* Hero */}
      <section className="py-12 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-rm-text-secondary hover:text-rm-primary transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-3 py-1 bg-rm-accent/20 text-rm-primary text-sm font-medium rounded-full mb-4">
              {post.category}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold text-rm-text mb-6">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-rm-text-secondary">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.img
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            src={post.image_url}
            alt={post.title}
            className="w-full rounded-2xl shadow-lg"
          />
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-rm-text font-semibold mb-4">Partager cet article</p>
            <div className="flex items-center gap-3">
              <a 
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${post.title}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-rm-primary hover:text-white transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${post.title}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-rm-primary hover:text-white transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center hover:bg-rm-primary hover:text-white transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-rm-primary rounded-2xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">
              Besoin d'aide pour organiser ton entreprise ?
            </h3>
            <p className="text-white/80 mb-6">
              Découvre comment BatiAssist peut t'accompagner avec un assistant à mi-temps ou plein temps 
              et une équipe digitale complète.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-rm-secondary hover:bg-rm-secondary/90 text-white font-semibold rounded-full px-8 py-6">
                Réserver un entretien gratuit
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
